import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './shared/Card';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
type CareerResultsProps = {
  onContinue: () => void;
};
export const CareerResults: React.FC<CareerResultsProps> = ({
  onContinue
}) => {
  const [revealed, setRevealed] = useState<number[]>([]);
  const careers = [{
    title: 'UX Designer',
    match: 92,
    description: 'Your creative intuition and analytical mindset make you perfect for designing user experiences that delight and inspire.',
    strengths: ['Visual thinking', 'Empathy', 'Problem solving'],
    icon: '🎨'
  }, {
    title: 'Data Scientist',
    match: 87,
    description: 'Your natural pattern recognition abilities and logical approach are ideal for extracting insights from complex data.',
    strengths: ['Analytical thinking', 'Mathematical aptitude', 'Communication'],
    icon: '📊'
  }, {
    title: 'Product Manager',
    match: 84,
    description: 'Your leadership qualities and strategic vision make you well-suited to guiding product development and innovation.',
    strengths: ['Strategic planning', 'Team coordination', 'User advocacy'],
    icon: '🚀'
  }];
  useEffect(() => {
    // Reveal cards one by one with a delay
    const timer1 = setTimeout(() => setRevealed([0]), 500);
    const timer2 = setTimeout(() => setRevealed([0, 1]), 1500);
    const timer3 = setTimeout(() => setRevealed([0, 1, 2]), 2500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="text-center" data-id="element-636">
      <div className="mb-8" data-id="element-637">
        <h1 className="text-3xl font-bold text-white mb-3" data-id="element-638">
          Your Cosmic Career Matches
        </h1>
        <p className="text-[#B8C0E0]" data-id="element-639">
          Based on your unique cosmic profile, these careers align with your
          stars
        </p>
      </div>
      {/* Confetti effect would be added here in a real implementation */}
      <div className="space-y-6 max-w-xl mx-auto" data-id="element-640">
        {careers.map((career, index) => <motion.div key={index} initial={{
        opacity: 0,
        y: 20
      }} animate={revealed.includes(index) ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.5
      }} data-id="element-641">
            <Card className="relative overflow-hidden" data-id="element-642">
              {/* Cosmic background effect */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#7D56F4]/20 to-transparent rounded-full -mr-10 -mt-10" data-id="element-643"></div>
              <div className="flex items-start" data-id="element-644">
                <div className="text-4xl mr-4" data-id="element-645">{career.icon}</div>
                <div className="flex-1 text-left" data-id="element-646">
                  <h3 className="text-xl font-bold mb-1" data-id="element-647">{career.title}</h3>
                  <div className="flex items-center mb-3" data-id="element-648">
                    <div className="w-full bg-[#1C2541] rounded-full h-2 mr-2" data-id="element-649">
                      <motion.div className="h-2 rounded-full bg-gradient-to-r from-[#7D56F4] to-[#19D3DA]" initial={{
                    width: 0
                  }} animate={{
                    width: `${career.match}%`
                  }} transition={{
                    delay: 0.3,
                    duration: 1
                  }} data-id="element-650"></motion.div>
                    </div>
                    <span className="text-lg font-bold text-[#19D3DA]" data-id="element-651">
                      {career.match}%
                    </span>
                  </div>
                  <p className="text-[#B8C0E0] text-sm mb-4" data-id="element-652">
                    {career.description}
                  </p>
                  <div className="mb-2 text-sm font-medium" data-id="element-653">
                    Key Cosmic Strengths:
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-654">
                    {career.strengths.map((strength, i) => <div key={i} className="bg-[#1C2541] rounded-full px-3 py-1 text-xs flex items-center" data-id="element-655">
                        <StarIcon size={12} className="text-[#FFD700] mr-1" data-id="element-656" />
                        {strength}
                      </div>)}
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>)}
      </div>
      <div className="mt-8" data-id="element-657">
        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={onContinue} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-3 px-8 rounded-full flex items-center mx-auto" data-id="element-658">
          <span data-id="element-659">Explore Your Career Journey</span>
          <ArrowRightIcon size={18} className="ml-2" data-id="element-660" />
        </motion.button>
      </div>
    </motion.div>;
};